import React, { useEffect, useState, FC } from "react"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { connect } from "react-redux"
import { RootState } from "../../../../store"
import { checkAdminPermission } from "../../../../utility"
import { ADMIN_PERMISSIONS } from "../../../../constants"
import AdminMissingShareHolders from "./missing-share-holders/AdminMissingShareHolders"
import {
  fetchMissingTrackShareHolderCount,
  fetchRoyaltySummaryDuplicateCount,
  updateMissingTrackShareHolderCount,
  updateRoyaltySummaryDuplicateCount
} from "../../../../actions/adminActions"
import RoyaltySummaryDuplicate from "./royalty-summary-duplicate/RoyaltySummaryDuplicate"
import Settings from "./settings/Settings"
import AdminMissingYoutubeVideos from "./missing-youtube-videos/AdminMissingYoutubeVideos"
import IntercomKeywords from "./intercom-keywords/IntercomKeywords"
import EmailTemplates from "./email-templates/EmailTemplates"

enum TabOptions {
  MISSING_SHARE_HOLDERS = "Missing Track Share Holders",
  ROYALY_SUMMARY_DUPLICATE = "Royalty Summary Duplicate",
  MISSING_YOUTUBE_VIDEOS = "Missing Youtube Videos",
  SETTINGS = "Settings",
  INTERCOM_KEYWORDS = "Intercom Keywords",
  EMAIL_TEMPLATES = "Email Templates"
}

type AdminActionsProps = {
  userAdminPermissions?: string
  missingTrackShareHolderCount: number
  fetchMissingTrackShareHolderCount: () => Promise<void>
  updateMissingTrackShareHolderCount: (count: number) => Promise<void>
  royaltySummaryDuplicateCount: number
  fetchRoyaltySummaryDuplicateCount: () => Promise<void>
  updateRoyaltySummaryDuplicateCount: (count: number) => Promise<void>
}

const AdminActions: FC<AdminActionsProps> = (props) => {
  const [openSidebar, setOpenSidebar] = useState<boolean>(true)
  const [selectedTab, setSelectedTab] = useState<TabOptions>()

  useEffect(() => {
    if (
      checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_MISSING_SHARE_HOLDER, props.userAdminPermissions) &&
      !props.missingTrackShareHolderCount
    ) {
      props.fetchMissingTrackShareHolderCount()
    }

    if (
      checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_ROYALTY_SUMMARY_DUPLICATE, props.userAdminPermissions) &&
      !props.royaltySummaryDuplicateCount
    ) {
      props.fetchRoyaltySummaryDuplicateCount()
    }

    if (checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_MISSING_SHARE_HOLDER, props.userAdminPermissions)) {
      setSelectedTab(TabOptions.MISSING_SHARE_HOLDERS)
    } else if (checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_ROYALTY_SUMMARY_DUPLICATE, props.userAdminPermissions)) {
      setSelectedTab(TabOptions.ROYALY_SUMMARY_DUPLICATE)
    } else if (checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_MISSING_YOUTUBE_VIDEO, props.userAdminPermissions)) {
      setSelectedTab(TabOptions.MISSING_YOUTUBE_VIDEOS)
    } else if (checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_SETTINGS, props.userAdminPermissions)) {
      setSelectedTab(TabOptions.SETTINGS)
    }
  }, [])

  return (
    <div className="catalog flex-grow justify-center">
      <div className="w-[100%] flex ">
        <div
          className={`${
            openSidebar ? "min-w-[230px]" : "min-w-0 !w-[0px]"
          } max-w-[320px] w-[20%] bg-[#F7F7F7] min-h-[100vh] flex flex-col overflow-hidden shrink-0`}
          style={{
            transitionDuration: "0.5s"
          }}
        >
          <div>
            <div className="py-1.5 px-4">
              <div className="p-2">
                <KTitleSpan text="Admin Actions" color="#111" fontSize={20} lineHeight="28px" />
              </div>
              {checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_MISSING_SHARE_HOLDER, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-user-advance"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.MISSING_SHARE_HOLDERS ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.MISSING_SHARE_HOLDERS) return
                    setSelectedTab(TabOptions.MISSING_SHARE_HOLDERS)
                  }}
                >
                  <KSpan
                    text={`Missing Track Share Holders (${props.missingTrackShareHolderCount})`}
                    fontSize={12}
                    lineHeight="20px"
                    fontWeight={500}
                    color="#111"
                  />
                </div>
              )}
              {checkAdminPermission(
                ADMIN_PERMISSIONS.ACTIONS_ROYALTY_SUMMARY_DUPLICATE,
                props.userAdminPermissions
              ) && (
                <div
                  data-testid="left-bar-royalty-summary-duplicate"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.ROYALY_SUMMARY_DUPLICATE ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.ROYALY_SUMMARY_DUPLICATE) return
                    setSelectedTab(TabOptions.ROYALY_SUMMARY_DUPLICATE)
                  }}
                >
                  <KSpan
                    text={`Royalty Summary Duplicate (${props.royaltySummaryDuplicateCount})`}
                    fontSize={12}
                    lineHeight="20px"
                    fontWeight={500}
                    color="#111"
                  />
                </div>
              )}
              {checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_MISSING_YOUTUBE_VIDEO, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-settings"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.MISSING_YOUTUBE_VIDEOS ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.MISSING_YOUTUBE_VIDEOS) return
                    setSelectedTab(TabOptions.MISSING_YOUTUBE_VIDEOS)
                  }}
                >
                  <KSpan
                    text={`Missing Youtube Videos`}
                    fontSize={12}
                    lineHeight="20px"
                    fontWeight={500}
                    color="#111"
                  />
                </div>
              )}
              {checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_SETTINGS, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-settings"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.SETTINGS ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.SETTINGS) return
                    setSelectedTab(TabOptions.SETTINGS)
                  }}
                >
                  <KSpan text={`Settings`} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}
              {checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_SETTINGS, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-intercom-keywords"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.INTERCOM_KEYWORDS ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.INTERCOM_KEYWORDS) return
                    setSelectedTab(TabOptions.INTERCOM_KEYWORDS)
                  }}
                >
                  <KSpan text={`Intercom Keywords`} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}
              {checkAdminPermission(ADMIN_PERMISSIONS.ACTIONS_SETTINGS, props.userAdminPermissions) && (
                <div
                  data-testid="left-bar-email-templates"
                  className={`rounded-[10px] p-2 flex flex-row gap-2 mt-4 items-center ${
                    selectedTab === TabOptions.EMAIL_TEMPLATES ? "bg-[#FFF]" : "transparent"
                  } cursor-pointer whitespace-nowrap`}
                  onClick={() => {
                    if (selectedTab === TabOptions.EMAIL_TEMPLATES) return
                    setSelectedTab(TabOptions.EMAIL_TEMPLATES)
                  }}
                >
                  <KSpan text={`Email Templates`} fontSize={12} lineHeight="20px" fontWeight={500} color="#111" />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grow min-h-0">
          {selectedTab === TabOptions.MISSING_SHARE_HOLDERS && (
            <AdminMissingShareHolders
              openSidebar={openSidebar}
              setOpenSidebar={setOpenSidebar}
              missingTrackShareHolderCount={props.missingTrackShareHolderCount}
              updateMissingTrackShareHolderCount={props.updateMissingTrackShareHolderCount}
            />
          )}
          {selectedTab === TabOptions.ROYALY_SUMMARY_DUPLICATE && (
            <RoyaltySummaryDuplicate openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.MISSING_YOUTUBE_VIDEOS && (
            <AdminMissingYoutubeVideos openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.SETTINGS && (
            <Settings openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.INTERCOM_KEYWORDS && (
            <IntercomKeywords openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
          {selectedTab === TabOptions.EMAIL_TEMPLATES && (
            <EmailTemplates openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    userAdminPermissions: state.auth.user.adminPermissions,
    missingTrackShareHolderCount: state.admin.missingTrackShareHolderCount,
    royaltySummaryDuplicateCount: state.admin.royaltySummaryDuplicateCount
  }
}

export default connect(mapStateToProps, {
  fetchMissingTrackShareHolderCount,
  updateMissingTrackShareHolderCount,
  fetchRoyaltySummaryDuplicateCount,
  updateRoyaltySummaryDuplicateCount
})(AdminActions)
